// Copyright (c) Mysten Labs, Inc. — DGC: LOCAL homepage shows configured asset list
// SPDX-License-Identifier: Apache-2.0

import clsx from 'clsx';
import { color } from 'framer-motion';
import { lazy } from 'react';

import { AccountsCardGraph } from '~/components/AccountCardGraph';
import { Activity } from '~/components/Activity';
import { CpcChainTradesGraph } from '~/components/cpc/CpcChainTradesGraph';
import { CpcCurrentEpoch } from '~/components/cpc/CpcCurrentEpoch';
import { CpcMarketParticipantsGraph } from '~/components/cpc/CpcMarketParticipantsGraph';
import { CpcNetworkActivity } from '~/components/cpc/CpcNetworkActivity';
import { CpcLeaderboardsCard } from '~/components/cpc/CpcLeaderboardsCard';
import { CpcPopularPackagesCard } from '~/components/cpc/CpcPopularPackagesCard';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { CurrentEpoch, OnTheNetwork } from '~/components/HomeMetrics';
import { PageLayout } from '~/components/Layout/PageLayout';
import { SuiTokenCard } from '~/components/SuiTokenCard';
import { TopPackagesCard } from '~/components/top-packages/TopPackagesCard';
import { TopValidatorsCard } from '~/components/top-validators-card/TopValidatorsCard';
import { TransactionsCardGraph } from '~/components/TransactionsCardGraph';
import { useNetwork } from '~/context';
import { TabHeader } from '~/ui/Tabs';
import { Network } from '~/utils/api/DefaultRpcClient';

const ValidatorMap = lazy(() => import('../../components/validator-map'));

const TRANSACTIONS_LIMIT = 25;

function Home() {
	const [network] = useNetwork();
	const isSuiTokenCardEnabled = network === Network.MAINNET;
	const isLocal = network === Network.LOCAL;

	return (
		<PageLayout
			gradient={{
				content: (
					<div
						data-testid="home-page"
						className={clsx(
							'home-page-grid-container-top',
							isSuiTokenCardEnabled && 'with-token',
						)}
					>
						<div style={{ gridArea: 'network' }} className="overflow-hidden">
							{isLocal ? <CpcNetworkActivity /> : <OnTheNetwork />}
						</div>
						<div style={{ gridArea: 'epoch' }}>
							{isLocal ? <CpcCurrentEpoch /> : <CurrentEpoch />}
						</div>
						{isSuiTokenCardEnabled ? (
							<div style={{ gridArea: 'token' }}>
								<SuiTokenCard />
							</div>
						) : null}
						<div style={{ gridArea: 'transactions' }}>
							{isLocal ? <CpcChainTradesGraph /> : <TransactionsCardGraph />}
						</div>
						<div style={{ gridArea: 'accounts' }}>
							{isLocal ? <CpcMarketParticipantsGraph /> : <AccountsCardGraph />}
						</div>
					</div>
				),
				size: 'lg',
			}}
			content={
				<div className={clsx('home-page-grid-container-bottom', isLocal && 'local-cpc')}>
					<div style={{ gridArea: 'activity' }}>
						<ErrorBoundary>
							<Activity initialLimit={TRANSACTIONS_LIMIT} disablePagination />
						</ErrorBoundary>
					</div>
					<div style={{ gridArea: 'packages' }}>
						{isLocal ? <CpcPopularPackagesCard /> : <TopPackagesCard />}
					</div>
					<div data-testid="validators-table" style={{ gridArea: 'validators' }} className="min-w-0">
						<TabHeader title={isLocal ? 'Leaderboards' : 'Validators'}>
							<ErrorBoundary>
								{isLocal ? (
									<CpcLeaderboardsCard limit={8} />
								) : (
									<TopValidatorsCard limit={10} showIcon />
								)}
							</ErrorBoundary>
						</TabHeader>
					</div>
				</div>
			}
		/>
	);
}

export default Home;
